import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';

import api from '../../lib/api';

import type { CreateProductPayload, ProductListParams, ProductListResponse } from './types';

export * from './types';

export function useListProducts(params: ProductListParams) {
  const p = {
    sortingKey: params.sortingKey ?? 'updatedAt',

    sortingOrder: params.sortingOrder ?? 'desc',

    searchQuery: params.searchQuery ?? '',

    page: params.page ?? 1,

    pageSize: params.pageSize ?? 10,

    locationId: params.locationId ?? '',

    loadStatus: params.loadStatus ?? 'all',

    problemFirst: params.problemFirst ?? false,

    archived: params.archived ?? 'include',

    customerId: params.customerId ?? '',
  };

  return useQuery<ProductListResponse>({
    queryKey: ['products', p],

    queryFn: async () => {
      const { data } = await api.get('/organization/products', {
        params: p,
      });

      return data as ProductListResponse;
    },

    placeholderData: keepPreviousData,

    staleTime: 5_000,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      const fd = new FormData();

      fd.append('name', payload.name);

      /*
       * SKU is optional on product creation.
       *
       * buildCreatePayload() already trims the
       * canonical SKU and omits it when empty.
       */
      if (payload.sku) {
        fd.append('sku', payload.sku);
      }

      if (payload.description) {
        fd.append('description', payload.description);
      }

      fd.append('deliveryType', payload.deliveryType);

      /*
       * Includes:
       *
       * - locationId
       * - quantity
       * - stonePriceCents
       */
      fd.append('stocks', JSON.stringify(payload.stocks));

      if (payload.deliveryType === 'PALLET') {
        fd.append('palletSizes', JSON.stringify(payload.palletSizes ?? []));
      }

      /*
       * Do NOT append loadOptions here.
       *
       * The current product-create API does not read
       * this field, and CreateProductPayload keeps it
       * only for backwards compatibility.
       */

      fd.append('trackInventory', String(payload.trackInventory ?? false));

      fd.append('defaultLocationId', payload.defaultLocationId ?? '');

      if (payload.image) {
        fd.append('image', payload.image);
      }

      const response = await api.post('/organization/products', fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],

        exact: false,
      });
    },
  });
}

export function useArchiveProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/organization/products/${id}/archive`)).data,

    onSuccess: (_data, id) => {
      toast.success('Product archived.');

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['product', id],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],

        exact: false,
      });
    },
  });
}

export function useUnarchiveProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/organization/products/${id}/unarchive`)).data,

    onSuccess: (_data, id) => {
      toast.success('Product unarchived.');

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['product', id],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],

        exact: false,
      });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/organization/products/${id}`);
    },

    onSuccess: () => {
      toast.success('Product deleted.');

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],

        exact: false,
      });
    },

    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.error('Product is used in orders. Archive instead.');

        return;
      }

      toast.error('Failed to delete product.');
    },
  });
}
